/**
 * Player-placeable portal pair with FULL momentum carry. A body crossing a
 * portal disc is re-emitted from the twin with its speed preserved and its
 * direction remapped through the portals' relative orientation (standard
 * portal transform: rotate π about the local up axis between frames).
 *
 * Detection is analytic (prev/curr plane crossing), not collider-based, so
 * fast projectiles can never tunnel past the sensor.
 *
 * M2: `place()` re-anchors an end on any static surface (Q = blue entry,
 * R = orange exit). Per-end cooldown + minimum separation stop teleport loops.
 */
import * as THREE from "three";
import type { TrashPiece } from "./Trash.js";
import { PALETTE } from "../render/theme.js";
import { PORTAL_REARM_TICKS } from "../simulation/types.js";

const MIN_SEPARATION = 3; // m between portal centers
const TRASH_RADIUS = 0.45; // effective body radius for trash crossing tests
/** Shared portal re-arm cooldown (LOCKED-DESIGN 3.4/18.10) in seconds. */
const PORTAL_REARM_SECONDS = PORTAL_REARM_TICKS / 60;

export interface PortalEnd {
  id: "A" | "B";
  pos: THREE.Vector3;
  quat: THREE.Quaternion; // ring frame; local +Z is the outward normal
  normal: THREE.Vector3;
  radius: number;
  placeCooldown: number;
  visual: THREE.Group;
  /** the raycastable swirl disc (aim target) for this end */
  disc: THREE.Mesh;
  /**
   * Presentation/enable gate (LOCKED-DESIGN 18.6 enforced glass beat): a
   * portal that is not `live` is hidden, inert to traversal, and excluded
   * from the aim ray so the surface behind it stays placeable. Real runs keep
   * both ends live from the placement countdown on, so this is a no-op there
   * and cannot move any recorded traversal (determinism-frozen).
   */
  live: boolean;
  pending: { pos: THREE.Vector3; normal: THREE.Vector3 } | null;
}

export interface Traversable {
  /** world position before this step */
  prevPos: THREE.Vector3;
  /** current world position */
  currPos: THREE.Vector3;
  /** current linear velocity */
  vel: THREE.Vector3;
}

/**
 * Capture scoping for the SLOW path (PLAYTEST F5, LOCKED-DESIGN 18.17).
 *
 * - `"strict"` (default) is the historical window: the body centre must be at
 *   most 0.2 m behind the disc plane and must not already be moving outward.
 *   The PLAYER traversal and the re-arm clearance test both stay on this, so
 *   the F2/F7 accidental-round-trip window is byte-unchanged by the F5 fix.
 * - `"inward"` is TRASH-ONLY, and further scoped to the ONE piece and the ONE
 *   moment it exists for. A held piece floats `GrabBeam.HOLD_DIST` (2.2 m) in
 *   front of the player, so a throw made closer than that at a wall/panel
 *   portal STARTS past the plane: no sign change for the fast path, and the
 *   strict slow path rejects it as "behind and leaving". `"inward"` widens the
 *   behind-plane tolerance to the body's own radius plus a margin, and replaces
 *   the blanket outward rejection with two narrower ones: a body inside the
 *   STRICT window and moving away is still refused (identical to strict), and a
 *   body deeper than the strict window must actually be travelling THROUGH the
 *   disc.
 *
 *   Direction of travel alone cannot tell "through the disc" from "away behind
 *   it", so the mode is granted per piece by `captureModeFor`: only a release
 *   that `GrabBeam.clampReleaseOrigin` actually re-solved (aimed at the disc,
 *   point-blank) carries a `portalInward` window, and only for
 *   PORTAL_INWARD_WINDOW_SECONDS. Ricochets, over-the-top near misses
 *   descending behind the ring, and back-side throws therefore stay on strict
 *   capture and are never quietly delivered.
 */
export type CaptureMode = "strict" | "inward";

/**
 * How long (seconds) a point-blank release keeps the widened `"inward"` window.
 * Covers the whole flight of such a throw with slop: the clamped solve targets
 * the disc centre from at most RELEASE_MAX_BEHIND + RELEASE_FRONT_MARGIN (2 m)
 * away, and `GrabBeam.ballisticVelocity` floors the flight time at ARC_T_MIN
 * (0.45 s), so the piece reaches the plane well inside this window.
 */
export const PORTAL_INWARD_WINDOW_SECONDS = 0.75;

/**
 * The capture mode a trash body is entitled to this step (see CaptureMode).
 * Pure, so the scoping rule is unit-pinned without Rapier or a renderer.
 */
export function captureModeFor(portalInward: number): CaptureMode {
  return portalInward > 0 ? "inward" : "strict";
}

/** Behind-plane tolerance shared by both modes (the historical window). */
const STRICT_BEHIND = -0.2;
/**
 * How far behind a disc an `"inward"` release may start, beyond the body's own
 * radius. DERIVED, not tuned: `GrabBeam` floats the held volley HOLD_DIST
 * (2.2 m) ahead of the player and the player capsule (radius ~1 m) cannot stand
 * closer than that to a surface, so the deepest a thrown piece can be released
 * behind a horizontal-normal disc is 2.2 − 1.0 = 1.2 m. The rest covers the
 * first post-release step of travel (a throw paces 14 m/s ≈ 0.23 m per 60 Hz
 * step), so the first sample after the release is still inside the window.
 */
const INWARD_BEHIND_MARGIN = 1.2;
/**
 * Minimum closing speed (m/s along −normal) an `"inward"` body must carry to be
 * taken from BEHIND the strict window. This is what keeps the widening scoped
 * to actual throws: loose debris resting or rolling behind a panel is NOT
 * quietly delivered, because it is not moving through the disc.
 */
const INWARD_MIN_CLOSING = 0.5;

/** Result of a traversal computation, to be applied by the owner. */
export interface TraverseResult {
  from: PortalEnd;
  to: PortalEnd;
  outPos: THREE.Vector3;
  outVel: THREE.Vector3;
}

export class PortalPair {
  readonly group = new THREE.Group();
  readonly A: PortalEnd;
  readonly B: PortalEnd;
  /** meshes the aim ray may target (the discs) */
  readonly aimTargets: THREE.Object3D[] = [];

  // scratch
  private readonly v1 = new THREE.Vector3();
  private readonly v2 = new THREE.Vector3();
  private readonly v3 = new THREE.Vector3();
  private readonly q1 = new THREE.Quaternion();
  private readonly trv: Traversable = {
    prevPos: new THREE.Vector3(),
    currPos: new THREE.Vector3(),
    vel: new THREE.Vector3(),
  };
  private readonly result: TraverseResult;

  private readonly defaults: { pos: THREE.Vector3; normal: THREE.Vector3 }[];

  constructor(
    aPos: THREE.Vector3,
    aNormal: THREE.Vector3,
    bPos: THREE.Vector3,
    bNormal: THREE.Vector3,
    radius = 1.9,
  ) {
    this.A = this.makeEnd("A", aPos, aNormal, radius, PALETTE.anchorBlue);
    this.B = this.makeEnd("B", bPos, bNormal, radius, PALETTE.receiverOrange);
    this.defaults = [
      { pos: aPos.clone(), normal: aNormal.clone().normalize() },
      { pos: bPos.clone(), normal: bNormal.clone().normalize() },
    ];
    this.result = {
      from: this.A,
      to: this.B,
      outPos: new THREE.Vector3(),
      outVel: new THREE.Vector3(),
    };
  }

  private readonly discMats: THREE.ShaderMaterial[] = [];
  private readonly discByEnd = new Map<"A" | "B", THREE.Mesh>();

  /** Render-side only: swirl animation. */
  animate(t: number): void {
    for (const m of this.discMats) {
      const u = m.uniforms.uTime;
      if (u) u.value = t;
    }
  }

  private makeEnd(
    id: "A" | "B",
    pos: THREE.Vector3,
    normal: THREE.Vector3,
    radius: number,
    color: number,
  ): PortalEnd {
    const visual = new THREE.Group();
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(radius, 0.12, 12, 48),
      new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 2.2,
        roughness: 0.35,
      }),
    );
    visual.add(ring);
    // swirling energy surface
    const discMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(color) },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform vec3 uColor;
        varying vec2 vUv;
        void main() {
          vec2 p = vUv * 2.0 - 1.0;
          float r = length(p);
          if (r > 1.0) discard;
          float a = atan(p.y, p.x);
          // two counter-rotating spiral bands
          float s1 = sin(a * 3.0 - uTime * 2.6 + r * 9.0);
          float s2 = sin(a * 5.0 + uTime * 1.9 - r * 14.0);
          float swirl = 0.5 + 0.3 * s1 + 0.2 * s2;
          float core = smoothstep(0.55, 0.0, r);           // bright center
          float edge = smoothstep(0.14, 0.0, abs(r - 0.9)); // inner rim
          float body = smoothstep(1.0, 0.25, r);
          vec3 col = uColor * (body * swirl * 0.9 + core * 1.4 + edge * 1.8);
          float alpha = body * 0.85 + edge;
          gl_FragColor = vec4(col * 1.6, alpha);
        }
      `,
    });
    this.discMats.push(discMat);
    const disc = new THREE.Mesh(new THREE.CircleGeometry(radius * 0.94, 48), discMat);
    visual.add(disc);
    this.aimTargets.push(disc);
    this.discByEnd.set(id, disc);
    const glow = new THREE.PointLight(color, 7, 13, 1.8);
    glow.position.set(0, 0, 0.4);
    visual.add(glow);
    this.group.add(visual);

    const end: PortalEnd = {
      id,
      pos: pos.clone(),
      quat: new THREE.Quaternion(),
      normal: normal.clone().normalize(),
      radius,
      placeCooldown: 0,
      visual,
      disc,
      live: true,
      pending: null,
    };
    this.syncEnd(end);
    return end;
  }

  private syncEnd(end: PortalEnd): void {
    end.quat.setFromUnitVectors(new THREE.Vector3(0, 0, 1), end.normal);
    end.visual.position.copy(end.pos);
    end.visual.quaternion.copy(end.quat);
  }

  update(dt: number): void {
    for (const end of [this.A, this.B]) {
      if (end.placeCooldown > 0) end.placeCooldown -= dt;
      if (end.pending && end.placeCooldown <= 0) {
        end.pos.copy(end.pending.pos); end.normal.copy(end.pending.normal); end.pending = null; this.syncEnd(end);
      }
    }
  }

  /** Back to run-start positions (run reset). */
  resetDefaults(): void {
    const [a, b] = this.defaults;
    if (!a || !b) return;
    this.A.pos.copy(a.pos);
    this.A.normal.copy(a.normal);
    this.A.placeCooldown = 0;
    this.A.pending = null;
    this.syncEnd(this.A);
    this.B.pos.copy(b.pos);
    this.B.normal.copy(b.normal);
    this.B.placeCooldown = 0;
    this.B.pending = null;
    this.syncEnd(this.B);
  }

  /** Both ends live together (real runs; graduation reveal). */
  get bothLive(): boolean {
    return this.A.live && this.B.live;
  }

  /**
   * Reveal/conceal one end (LOCKED-DESIGN 18.6). Concealed ends are hidden and
   * inert: no traversal (see `step`/`isClearOfPortals` callers) and excluded
   * from `raycastTargets` so the surface behind stays placeable.
   */
  setLive(id: "A" | "B", live: boolean): void {
    const end = id === "A" ? this.A : this.B;
    end.live = live;
    end.visual.visible = live;
  }

  setBothLive(live: boolean): void {
    this.setLive("A", live);
    this.setLive("B", live);
  }

  /** Discs the aim ray may hit — only LIVE ends (concealed portals never block placement). */
  raycastTargets(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    if (this.A.live) out.push(this.A.disc);
    if (this.B.live) out.push(this.B.disc);
    return out;
  }

  /**
   * Resolve a pending placement immediately (tutorial teaching only): the
   * portal snaps to where it was aimed with no arming slide, so a first-time
   * placement reads as a crisp "it went there" beat.
   */
  snapPending(id: "A" | "B"): void {
    const end = id === "A" ? this.A : this.B;
    if (end.pending) {
      end.pos.copy(end.pending.pos);
      end.normal.copy(end.pending.normal);
      end.pending = null;
    }
    end.placeCooldown = 0;
    this.syncEnd(end);
  }

  resetEndToDefault(id: "A" | "B"): void {
    const index = id === "A" ? 0 : 1;
    const stored = this.defaults[index];
    const end = id === "A" ? this.A : this.B;
    if (!stored) return;
    end.pos.copy(stored.pos); end.normal.copy(stored.normal); end.pending = null; end.placeCooldown = 0; this.syncEnd(end);
  }

  followEnd(id: "A" | "B", pos: THREE.Vector3, normal: THREE.Vector3): void {
    const end = id === "A" ? this.A : this.B;
    if (end.pending) return;
    end.pos.copy(pos).addScaledVector(normal, .06); end.normal.copy(normal).normalize(); this.syncEnd(end);
  }

  /**
   * Re-anchor an end on a surface point. Returns false when rejected
   * (cooldown, or too close to the twin).
   */
  place(id: "A" | "B", pos: THREE.Vector3, normal: THREE.Vector3): boolean {
    const end = id === "A" ? this.A : this.B;
    const other = id === "A" ? this.B : this.A;
    if (end.placeCooldown > 0 || end.pending) return false;
    this.v1.copy(pos).addScaledVector(normal, 0.06); // hover just off the surface
    if (this.v1.distanceTo(other.pos) < MIN_SEPARATION) return false;
    end.pending = { pos: this.v1.clone(), normal: normal.clone().normalize() };
    end.placeCooldown = .75;
    return true;
  }

  /**
   * Check + apply traversal for every live piece. Call AFTER world.step().
   * Returns pieces that traversed this step (for FX/scoring hooks).
   */
  step(pieces: TrashPiece[]): TrashPiece[] {
    const traversed: TrashPiece[] = [];
    // A portal needs BOTH ends: while either is concealed (the glass beat and
    // the choose beats), nothing traverses (LOCKED-DESIGN 18.6). No-op in real
    // runs, where both ends are live from the placement countdown on.
    if (!this.bothLive) return traversed;
    for (const p of pieces) {
      if (!p.live || p.held) continue;
      const cur = p.body.translation();
      this.v1.set(cur.x, cur.y, cur.z);
      if (!p.portalArmed) {
        // re-arm only once the re-arm cooldown elapses AND the body is fully
        // clear of both portals — kills the instant re-loop and ping-pong
        // (LOCKED-DESIGN 3.4/18.10; portalCooldown is decremented by
        // TrashSpawner.updateCooldowns each fixed step).
        if (p.portalCooldown <= 0 && this.isClearOfPortals(this.v1, TRASH_RADIUS)) p.portalArmed = true;
        continue;
      }
      const lv = p.body.linvel();
      this.trv.prevPos.copy(p.prevPos);
      this.trv.currPos.copy(this.v1);
      this.trv.vel.set(lv.x, lv.y, lv.z);
      // TRASH-ONLY inward capture (PLAYTEST F5): a point-blank throw at a
      // wall/panel portal starts past the plane. Granted per piece and only for
      // the flight of that throw (GrabBeam stamps `portalInward` on a release it
      // re-solved), so every other body — including this same piece a second
      // later — is on the historical strict window. The player path at
      // Engine.computeTraversal stays on the default "strict" mode.
      const r = this.computeTraversal(this.trv, 0, TRASH_RADIUS, captureModeFor(p.portalInward));
      if (r) {
        p.body.setTranslation({ x: r.outPos.x, y: r.outPos.y, z: r.outPos.z }, true);
        p.body.setLinvel({ x: r.outVel.x, y: r.outVel.y, z: r.outVel.z }, true);
        p.prevPos.copy(r.outPos); // snap interpolation history — never smear
        p.portalArmed = false;
        p.portalCooldown = PORTAL_REARM_SECONDS;
        traversed.push(p);
      }
    }
    return traversed;
  }

  /**
   * Shallow cylinder over the disc: inside = this body gets taken. `capture`
   * scopes only the BEHIND-the-plane tolerance (see CaptureMode); the front
   * depth and the radial ring test are identical in both modes.
   */
  private insideCapture(end: PortalEnd, pos: THREE.Vector3, bodyRadius: number, capture: CaptureMode = "strict"): boolean {
    this.v2.copy(pos).sub(end.pos);
    const s = this.v2.dot(end.normal);
    const behind = capture === "inward" ? -(bodyRadius + INWARD_BEHIND_MARGIN) : STRICT_BEHIND;
    if (s < behind || s > bodyRadius + 0.55) return false;
    this.v2.addScaledVector(end.normal, -s);
    // full visible ring counts — players judge "I'm on it" by the ring
    return this.v2.lengthSq() < end.radius ** 2;
  }

  /**
   * Outside both capture volumes (with margin) — used by the re-arm gate.
   * Deliberately STRICT for every caller (trash re-arm and player re-arm): a
   * looser clearance test would shorten the re-arm hold and re-open F2/F7.
   */
  isClearOfPortals(pos: THREE.Vector3, bodyRadius: number): boolean {
    this.v3.copy(pos);
    return (
      !this.insideCapture(this.A, this.v3, bodyRadius + 0.4) &&
      !this.insideCapture(this.B, this.v3, bodyRadius + 0.4)
    );
  }

  /**
   * Generic traversal test for anything with a prev/curr position and a
   * velocity (trash bodies, the player capsule). `exitClearance` pushes the
   * exit point further out along the exit normal (capsules need ~1m).
   * `bodyRadius` offsets the crossing plane outward so a portal triggers
   * when the body's SURFACE reaches it — without this, floor portals could
   * never fire (a resting body's center never descends to the disc plane).
   * `capture` scopes the slow path only and DEFAULTS to "strict" so the player
   * capture window can never be widened by accident (see CaptureMode).
   * Returns null when no portal was crossed this step.
   */
  computeTraversal(
    t: Traversable,
    exitClearance: number,
    bodyRadius: number,
    capture: CaptureMode = "strict",
  ): TraverseResult | null {
    return (
      this.tryTraverse(t, this.A, this.B, exitClearance, bodyRadius, capture) ??
      this.tryTraverse(t, this.B, this.A, exitClearance, bodyRadius, capture)
    );
  }

  private tryTraverse(
    t: Traversable,
    from: PortalEnd,
    to: PortalEnd,
    exitClearance: number,
    bodyRadius: number,
    capture: CaptureMode,
  ): TraverseResult | null {
    const sPrev = this.v1.copy(t.prevPos).sub(from.pos).dot(from.normal) - bodyRadius;
    const sCurr = this.v1.copy(t.currPos).sub(from.pos).dot(from.normal) - bodyRadius;

    let crossed = false;
    if (sPrev !== sCurr && sPrev * sCurr <= 0 && (Math.abs(sPrev) <= 3 || Math.abs(sCurr) <= 3)) {
      // fast path: the surface crossed the disc plane this step — check radially
      const k = sPrev / (sPrev - sCurr);
      this.v1.lerpVectors(t.prevPos, t.currPos, k);
      this.v2.copy(this.v1).sub(from.pos);
      this.v2.addScaledVector(from.normal, -this.v2.dot(from.normal));
      crossed = this.v2.lengthSq() <= from.radius * from.radius;
    }
    if (!crossed) {
      // slow path: capture volume — lets bodies WALK/roll/stand into a portal
      // (a flat walk onto a floor portal has zero normal motion and would
      // never satisfy the crossing test). The fast path above is IDENTICAL in
      // both capture modes, so no traversal that works today can change.
      if (!this.insideCapture(from, t.currPos, bodyRadius, capture)) return null;
      const outward = t.vel.dot(from.normal); // >0 = moving away from the disc
      if (capture === "inward") {
        const s = this.v1.copy(t.currPos).sub(from.pos).dot(from.normal);
        // Deeper than the strict window: only a body actually travelling
        // THROUGH the disc is taken (the F5 point-blank release). Debris that
        // merely came to rest behind the panel is left alone.
        if (s < STRICT_BEHIND && outward > -INWARD_MIN_CLOSING) return null;
        // Inside the STRICT window and leaving — byte-identical to strict, for
        // the whole strict window (including the shallow [-0.2, 0] band, where
        // a piece bouncing back OFF the panel used to be captured by inward and
        // refused by strict: a third, undocumented difference between the modes).
        if (s > STRICT_BEHIND && outward > 0.5) return null;
      } else if (outward > 0.5) return null; // already leaving it
    }

    // ---- portal transform: local flip (π about local Y), from-frame → to-frame ----
    this.q1.copy(from.quat).invert();

    // exit at the twin's CENTER: disc-offset carry stacked with momentum
    // drift made exits unpredictable (probe: off-center entry + drift =
    // clipped the maw lip). Center exits keep the fountain trustworthy.
    this.result.outPos.copy(to.pos).addScaledVector(to.normal, 0.7 + exitClearance);

    // velocity: speed preserved, direction remapped
    this.v3.copy(t.vel).applyQuaternion(this.q1);
    this.v3.x = -this.v3.x;
    this.v3.z = -this.v3.z;
    this.result.outVel.copy(this.v3).applyQuaternion(to.quat);

    this.result.from = from;
    this.result.to = to;
    return this.result;
  }
}
